import { routes } from '@/config/routes'
import { useBestPerformingVaultsQuery } from '@/queries/bestPerformingVaults.query'
import type { BenchmarkGroup } from '@/types/analytics'
import type { ChartPeriod } from '@/types/periods'
import {
  CHART_COLOR_VALUES,
  transformDataToBestPerformingVaultChartConfig,
} from '@/utils/analytics/transformDataToBestPerformingVaultChartConfig'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getValueColor } from '@/utils/styles/getValueColor'
import { formatAmount, formatDate } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { Link, generatePath } from 'react-router'
import { Button } from '../common/button'
import {
  DialogBody,
  DialogContentWrapper,
  DialogDescription,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
} from '../common/dialog'
import { Loader } from '../common/loader'
import { ProtocolImage } from '../common/protocolImage'
import { RadioGroup, RadioTile } from '../common/radio'
import { Separator } from '../common/separator'
import { BestPerformingVaultChart } from './bestPerformingVaultChart'

interface LeaderboardItem {
  rank: number
  name: string
  network: string
  vaultId: string
  protocol?: { name: string }
  change: number
  color: string
}

interface Props {
  chartPeriod: ChartPeriod
  group: BenchmarkGroup
  onGroupChange: (group: BenchmarkGroup) => void
}

export const BestPerformingVaultsFullView = ({ chartPeriod, group, onGroupChange }: Props) => {
  const {
    bestPerformingVaults: vaults,
    dataLastUpdated,
    isFetching,
  } = useBestPerformingVaultsQuery({
    period: chartPeriod,
    group,
  })

  const chartConfig = useMemo(() => {
    const config = transformDataToBestPerformingVaultChartConfig(vaults ?? [])
    return {
      ...config,
      series: [...config.series].sort((a, b) => b.totalChange - a.totalChange),
    }
  }, [vaults])

  const leaderboardItems: LeaderboardItem[] = useMemo(
    () =>
      (vaults ?? []).slice(0, 10).map((vault, i) => ({
        rank: i + 1,
        name: vault.vault.name,
        network: vault.vault.network,
        vaultId: vault.vault.vaultId,
        protocol: vault.vault.protocol,
        change: vault.sharePriceDiff,
        color: CHART_COLOR_VALUES[i % CHART_COLOR_VALUES.length],
      })),
    [vaults]
  )

  return (
    <DialogBody>
      <DialogHeaderWrapper className="pt-6 md:pt-8">
        <DialogHeader>
          <DialogTitle>Best performing vaults ($10M+ TVL)</DialogTitle>
          <DialogDescription>
            Change in share price over last {chartPeriod} days
            {dataLastUpdated && ` · Data last updated: ${formatDate({ timestamp: dataLastUpdated, showTime: true })}`}
          </DialogDescription>
        </DialogHeader>
        <Separator />
      </DialogHeaderWrapper>
      <DialogScrollArea>
        <DialogContentWrapper className="pt-4 md:pt-4">
          {isFetching || !vaults ? (
            <div className="flex min-h-64 items-center justify-center">
              <Loader />
            </div>
          ) : (
            <>
              <BestPerformingVaultChart config={chartConfig} hideLegend showBrush />
              <div className="mt-5 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold text-heading-foreground">Leaderboard</span>
                  <RadioGroup
                    value={group}
                    onValueChange={(v) => onGroupChange(v as BenchmarkGroup)}
                    className="ml-auto h-6 grid-cols-2 p-0.5"
                  >
                    <RadioTile id="full-view-usd" value="usd">
                      USD
                    </RadioTile>
                    <RadioTile id="full-view-eth" value="eth">
                      ETH
                    </RadioTile>
                  </RadioGroup>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  <div className="flex max-w-full flex-grow basis-64 flex-col gap-y-2">
                    {leaderboardItems.slice(0, 5).map((item) => (
                      <LeaderboardRow key={item.rank} item={item} />
                    ))}
                  </div>
                  <div className="flex max-w-full flex-grow basis-64 flex-col gap-y-2">
                    {leaderboardItems.slice(5, 10).map((item) => (
                      <LeaderboardRow key={item.rank} item={item} />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContentWrapper>
      </DialogScrollArea>
    </DialogBody>
  )
}

interface LeaderboardRowProps {
  item: LeaderboardItem
}

const LeaderboardRow = ({ item }: LeaderboardRowProps) => {
  return (
    <div className="flex min-h-10 items-center gap-2 rounded-xl text-sm">
      <span className="w-[18px] shrink-0 text-right text-body-foreground">{item.rank}</span>
      <div className="size-3 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
      {item.protocol && <ProtocolImage protocol={item.protocol} className="size-5 shrink-0" />}
      <Button
        variant="default"
        size="default"
        className="min-w-0 flex-grow items-baseline justify-start gap-1 text-heading-foreground max-xs:flex-wrap"
        asChild
      >
        <Link
          to={generatePath(routes.opportunity, { vaultId: item.vaultId, network: item.network })}
          target="_blank"
          rel="noopener noreferrer"
          prefetch="intent"
        >
          <p className="min-w-0 truncate text-sm">{item.name}</p>
          <span className="shrink-0 first-letter:uppercase">({getNetworkName(item.network)})</span>
        </Link>
      </Button>
      <span className={cn('shrink-0 font-semibold tabular-nums', getValueColor(item.change))}>
        {item.change > 0 && '+'}
        {formatAmount(item.change, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
      </span>
    </div>
  )
}
